import { memo, useCallback, useState } from "react";
import { Icon } from "@iconify/react";
import { Link, useLocation } from "react-router-dom";
import { colors, sidebar, type } from "../styles";
import { NAV_SECTIONS, NavItem } from "../routes";
import { useDashboardData } from "../hooks/useDashboardData";
import { fetchSyncState } from "../dashboardApi";
import { SyncStateResponse } from "../types";
import { formatRelative } from "../format";
import SegmentedToggle from "./SegmentedToggle";
import {
  RevenueBasis,
  setRevenueBasis,
  useRevenueBasis,
} from "../revenueBasis";

const REVENUE_BASIS_OPTIONS: { key: RevenueBasis; label: string }[] = [
  { key: "gross", label: "Gross" },
  { key: "net", label: "Net" },
];

// Visually modeled on the product's CreatorModeSidebar (194px sticky panel,
// 44px rows, 8px radius, #F4F5F6 for both hover and selected, 20px iconify
// icons) but imports nothing from it — internalTools folders may copy a
// pattern from src/components/, never depend on one. See this folder's README.

const SidebarRow = ({
  item,
  active,
}: {
  item: NavItem;
  active: boolean;
}) => {
  const [hovered, setHovered] = useState(false);
  const highlighted = item.enabled && (active || hovered);

  const row = (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        height: sidebar.itemHeight,
        paddingInline: sidebar.itemPaddingInline,
        borderRadius: sidebar.itemRadius,
        background: highlighted ? colors.surfaceMuted : "transparent",
        cursor: item.enabled ? "pointer" : "default",
        opacity: item.enabled ? 1 : 0.4,
        transition: "background-color 0.1s",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: sidebar.iconGap }}>
        <Icon icon={item.icon} height={sidebar.iconSize} style={{ color: colors.ink }} />
        <span
          style={{
            ...type.bodyMedium,
            fontWeight: active ? 600 : 500,
            color: colors.ink,
          }}
        >
          {item.label}
        </span>
      </div>
      {!item.enabled && (
        <span
          style={{
            ...type.bodySmallMedium,
            fontSize: 10,
            color: colors.inkFaint,
            border: `1px solid ${colors.border}`,
            borderRadius: 4,
            padding: "1px 5px",
          }}
        >
          Soon
        </span>
      )}
    </div>
  );

  if (!item.enabled || !item.path) {
    return row;
  }
  return (
    <Link to={item.path} style={{ textDecoration: "none" }}>
      {row}
    </Link>
  );
};

const AdminSidebar = memo(function AdminSidebarFn({
  adminName,
  adminEmail,
}: {
  adminName: string;
  adminEmail: string;
}) {
  const { pathname } = useLocation();

  // Fetched here rather than passed down: the sidebar outlives every screen, so
  // the freshness line shouldn't reset each time you navigate between tabs.
  const syncFetcher = useCallback(() => fetchSyncState(), []);
  const { data } = useDashboardData<SyncStateResponse>(syncFetcher, []);
  const syncState = data?.state ?? undefined;

  // Read from the module store rather than held here, so the switch survives
  // navigation between screens — `memo` on this component would otherwise let
  // a screen re-render against a basis the sidebar had already changed.
  const revenueBasis = useRevenueBasis();

  return (
    <nav
      style={{
        width: sidebar.width,
        flexShrink: 0,
        position: "sticky",
        top: 0,
        alignSelf: "flex-start",
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        background: colors.surface,
        borderRight: `0.5px solid ${colors.border}`,
      }}
    >
      {/* Header — identifies the tool, not the signed-in user (that's the
          footer). Not clickable: there is nowhere above Home to go. */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "16px 12px",
          marginInline: 10,
        }}
      >
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: 8,
            background: colors.ink,
            color: colors.surface,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            ...type.bodyBold,
            fontSize: 17,
            flexShrink: 0,
          }}
        >
          M
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
          <span style={{ ...type.bodyMedium, fontWeight: 600, color: colors.ink }}>
            Markit Admin
          </span>
          <span style={{ ...type.bodySmallRegular, color: colors.inkFaint }}>
            Internal tool
          </span>
        </div>
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: "0 10px" }}>
        {NAV_SECTIONS.map(section => (
          <div key={section.label} style={{ marginTop: 16 }}>
            <div
              style={{
                ...type.overline,
                color: colors.inkFaint,
                paddingInline: sidebar.itemPaddingInline,
                marginBottom: 4,
              }}
            >
              {section.label}
            </div>
            {section.items.map(item => (
              <SidebarRow
                key={item.label}
                item={item}
                active={Boolean(item.path) && pathname.startsWith(item.path!)}
              />
            ))}
          </div>
        ))}
      </div>

      {/* The dashboard-wide revenue basis.
          It lives here, above the footer, rather than on each revenue card,
          because it is one setting for the whole tool: Estimated MRR shown net
          beside a revenue chart still gross would put two numbers on different
          bases with nothing on either saying so — the same class of quiet
          mismatch the required `freshness` prop exists to prevent. The sidebar
          is the only surface present on every screen, and it already holds the
          other thing true of the whole page (the sync's freshness line). */}
      <div
        style={{
          borderTop: `0.5px solid ${colors.border}`,
          padding: "12px 16px",
          display: "flex",
          flexDirection: "column",
          gap: 6,
        }}
      >
        <div
          style={{
            ...type.overline,
            color: colors.inkFaint,
            paddingInline: 6,
          }}
        >
          Revenue basis
        </div>
        <SegmentedToggle<RevenueBasis>
          options={REVENUE_BASIS_OPTIONS}
          value={revenueBasis}
          onChange={setRevenueBasis}
          label="Revenue basis"
        />
        <span
          style={{ ...type.bodySmallRegular, color: colors.inkFaint, paddingInline: 6 }}
          title={
            revenueBasis === "net"
              ? "Every revenue figure on the dashboard has Stripe's own processing fee subtracted, read per charge from balance_transaction.fee. Days before the fee was tracked show — rather than a figure, because their fee was never measured."
              : "Every revenue figure on the dashboard is what Markit billed, before Stripe's processing fee. This is what every number here has always meant."
          }
        >
          {revenueBasis === "net"
            ? "Net of Stripe fees"
            : "Gross, before Stripe fees"}
        </span>
      </div>

      <div
        style={{
          borderTop: `0.5px solid ${colors.border}`,
          padding: "12px 22px",
          display: "flex",
          flexDirection: "column",
          gap: 6,
        }}
      >
        <span style={{ ...type.bodySmallMedium, color: colors.ink }}>{adminName}</span>
        <span
          style={{
            ...type.bodySmallRegular,
            color: colors.inkFaint,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={adminEmail}
        >
          {adminEmail}
        </span>
        {/* This dashboard is no longer all-live, so it must not say it is.
            Cards are individually chipped Live or Nightly sync; this line says
            when that sync last finished, and flags it when the last run didn't
            come back clean — otherwise a silently failing job just looks like a
            quiet week. */}
        <span
          style={{ ...type.bodySmallRegular, color: colors.inkFaint }}
          title={
            syncState
              ? `Nightly rollup ${syncState.status ?? "unknown"} · processed through ${
                  syncState.processedThroughDate || "nothing yet"
                }`
              : "The nightly rollup has not run yet. Cached cards will be empty."
          }
        >
          {syncState?.lastRunFinishedAt
            ? `Live + synced ${formatRelative(syncState.lastRunFinishedAt)}`
            : "Live · nightly sync pending"}
        </span>
        {syncState && syncState.status !== "ok" && (
          <span style={{ ...type.bodySmallRegular, color: colors.stateTrial }}>
            Last sync: {syncState.status}
          </span>
        )}
        <a
          href="/home"
          style={{
            ...type.bodySmallMedium,
            color: colors.inkMuted,
            textDecoration: "none",
            display: "flex",
            alignItems: "center",
            gap: 6,
            marginTop: 2,
          }}
        >
          <Icon icon="ion:exit-outline" height={14} />
          Exit to Markit
        </a>
      </div>
    </nav>
  );
});

export default AdminSidebar;
